import React, { useEffect, useState, useContext } from 'react'
import HeadTitle from '../../components/HeadTitle/HeadTitle'
import DataTable from '../../components/NewDataTable/NewDataTable'
import {
	IconButton,
	Icon,
	Tooltip,
	CircularProgress,
} from '@material-ui/core'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { dateDisplay } from '../../utils/Date'
import { TotalModalPopUpContext } from '../../contexts/TotalModalPopUpContext'
import TotalPopUpWarning from '../../components/TotalPopUpWarning/TotalPopUpWarning'
import {
	PLATFORM_ID_COOKIE,
	COMPANY_ID_COOKIE,
} from '../../constants/cookies'
import { getConvocationList } from '../../containers/DataContainer'
import PropTypes from 'prop-types'
import TYPES from '../../constants/pdpType'

const useStyles = makeStyles((theme) => ({
	validated: {
		color: '#4CAF50',
	},
	refuse: {
		color: '#e02214',
	},
	div: {
		textAlign: 'center',
	},
	dataTableContainer: {
		position: 'relative',
	},
	buttonProgress: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		marginTop: -12,
		marginLeft: -12,
	},
}))

function FollowConvocation() {
	const classes = useStyles()
	const [cookies] = useCookies()
	const { t } = useTranslation(['user'])

	const [datatable, setDatatable] = useState({ columns: [], data: [], options: {} })
	const { typeModal, setTypeModal } = useContext(TotalModalPopUpContext)
	const [openModal, setOpenModal] = useState(false)
	const [actualView, setActualView] = useState(null)
	const [isLoading, setIsLoading] = useState(true)

	/* Open a modal if the context contain a message */
	useEffect(() => {
		if (typeModal) setOpenModal(true)
	}, [typeModal])

	const closeModal = () => {
		setOpenModal(false)
		/* Flush the context */
		setTypeModal(null)
	}

	const getConvocationFollowUp = () => {
		setIsLoading(true)
		if (actualView || actualView === 0) {
			let data = {
				viewTypeId: actualView,
				nbItemsAsked: 1,
				pageNumber: 1,
				companyId: cookies[COMPANY_ID_COOKIE] !== "-1" ? parseInt(cookies[COMPANY_ID_COOKIE]) : 1
			}

			getConvocationList(
				cookies[PLATFORM_ID_COOKIE],
				data,
				(res, err) => {
					setIsLoading(false)
					if (err) {
						console.error('Error fetching convocation list:', err)
					}
					if (res && res.data) {
						// Filter visible columns for display and include invitation columns even if marked as excluded
						const visibleColumns = (res.data.columns || []).filter(col => 
							col.options && (col.options.display !== 'excluded' || 
							col.name === 'invitationInspectionCommune' || 
							col.name === 'invitationReunionCoordination')
						)
						
						// Transform object data to array format for DataTable
						const transformedData = {
							columns: visibleColumns,
							data: (res.data.data || []).map(item => {
								// Create array based on visible column order
								const row = visibleColumns.map(column => item[column.name] || '')
								// Add raw data as a special property for custom columns
								row._rawData = item
								return row
							}),
							options: {
								...res.data.options,
								selectableRows: 'none', // Disable row selection checkboxes
								print: false, // Disable print functionality
								// Use custom download function avec les données complètes passées directement
								onDownload: (buildHead, buildBody, p_Columns, p_Data) => {
									// Utiliser les données complètes de l'API directement
									const apiColumns = res.data.columns || []
									const apiData = res.data.data || []
									
									if (apiData.length === 0) {
										// Fallback standard
										const csvHeader = p_Columns.map(col => `"${col.name}"`).join(';')
										const csvRows = p_Data.map(row => {
											return row.data.map(cell => {
												const cellStr = String(cell || '').replace(/"/g, '""')
												return `"${cellStr}"`
											}).join(';')
										})
										const fullCSV = [csvHeader, ...csvRows].join('\n')
										return '\uFEFF' + fullCSV
									}
									
									// ORGANISER LES COLONNES : VISIBLES EN PREMIER, PUIS NON VISIBLES
									// Colonnes à exclure de l'export
									const excludedColumns = ['entId', 'pdpId', 'rpdNumero']
									
									// 1. Récupérer les noms des colonnes visibles dans l'ordre d'affichage
									const visibleColumnNames = visibleColumns.map(col => col.name)
									// Ajouter les colonnes customisées dans l'ordre d'affichage
									const nomEntrepriseIndex = visibleColumnNames.indexOf('nomEntreprise')
									if (nomEntrepriseIndex !== -1) {
										visibleColumnNames.splice(nomEntrepriseIndex + 1, 0, 'type')
									}
									visibleColumnNames.push('actions')
									
									// 2. Récupérer toutes les propriétés des données (sauf celles exclues)
									const allPropertyNames = new Set()
									apiData.forEach(item => {
										Object.keys(item).forEach(key => {
											if (!excludedColumns.includes(key)) {
												allPropertyNames.add(key)
											}
										})
									})
									
									// 3. Créer l'ordre final : colonnes visibles + colonnes non visibles
									const finalColumnOrder = []
									
									// D'abord les colonnes visibles dans l'ordre d'affichage (sauf celles exclues)
									visibleColumnNames.forEach(colName => {
										if (colName !== 'type' && colName !== 'actions' && 
											!excludedColumns.includes(colName) && 
											allPropertyNames.has(colName)) {
											finalColumnOrder.push(colName)
											allPropertyNames.delete(colName) // Supprimer pour éviter les doublons
											
											// Ajouter les colonnes représentant après invitationReunionCoordination
											if (colName === 'invitationReunionCoordination') {
												if (allPropertyNames.has('representantInspectionCommune')) {
													finalColumnOrder.push('representantInspectionCommune')
													allPropertyNames.delete('representantInspectionCommune')
												}
												if (allPropertyNames.has('representantReunionCoordination')) {
													finalColumnOrder.push('representantReunionCoordination')
													allPropertyNames.delete('representantReunionCoordination')
												}
											}
										}
									})
									
									// Ensuite les colonnes non visibles triées alphabétiquement (sauf celles exclues)
									const remainingColumns = Array.from(allPropertyNames).sort()
									finalColumnOrder.push(...remainingColumns)
									
									// Créer les colonnes dans l'ordre final
									const allColumns = finalColumnOrder.map(propName => ({
										name: propName,
										label: propName
									}))
									
									// Créer les données avec toutes les colonnes dans le bon ordre
									const allData = apiData.map(item => {
										const row = {
											data: allColumns.map(col => {
												let value = item[col.name] || ''
												
												// Formater les dates pour l'export CSV
												if ((col.name === 'dateInspectionPrealable' || col.name === 'dateReunionCoordination' || col.name === 'derniereModification') && value) {
													try {
														value = dateDisplay(value)
													} catch (e) {
														// Garder la valeur originale si le formatage échoue
													}
												}
												
												// Formater les valeurs booléennes pour l'export CSV
												if ((col.name === 'invitationInspectionCommune' || col.name === 'invitationReunionCoordination') && typeof value === 'boolean') {
													value = value ? 'Acceptée' : 'Refusée'
												}
												
												// Formater les valeurs entreprise pour l'export CSV
												if ((col.name === 'entreprisePrincipal' || col.name === 'entrepriseSousTraitante') && typeof value === 'boolean') {
													value = value ? 'Oui' : 'Non'
												}
												
												return value
											})
										}
										return row
									})
									
									// CONSTRUCTION MANUELLE DU CSV AVEC POINT-VIRGULE COMME SÉPARATEUR
									const csvHeader = allColumns.map(col => `"${col.name}"`).join(';')
									const csvRows = allData.map(row => {
										return row.data.map(cell => {
											const cellStr = String(cell || '').replace(/"/g, '""')
											return `"${cellStr}"`
										}).join(';')
									})
									
									const fullCSV = [csvHeader, ...csvRows].join('\n')
									return '\uFEFF' + fullCSV
								}
							},
							// Store all columns and raw data for CSV export
							allColumns: res.data.columns || [],
							allData: res.data.data || []
						}
						
						setDatatable(transformedData)
					}
				}
			)
		}
	}	

	const actionsColumn = {
		name: 'actions',
		options: {
			filter: false,
			filterType: 'textField',
			sort: false,
		},
	}

	const typeColumn = {
		name: 'type',
		options: {
			filter: false,
			filterType: 'dropdown',
			sort: false,
			filterOptions: {
				names: ['Principale', 'Sous-traitante', 'Principale et Sous-traitante', '-']
			}
		},
	}

	const FollowTable = () => {
		//#region Custom Columns

		const customDateColumn = (value, tableMeta) => {
			// Get the raw data to check the actual date value
			const rowIndex = tableMeta.rowIndex
			const rawData = datatable.data[rowIndex] && datatable.data[rowIndex]._rawData || {}
			
			// Try to get the date from raw data first
			const dateValue = rawData.derniereModification || value
			
			if (!dateValue) return ''
			return dateDisplay(dateValue)
		}

		const customBooleanColumn = (value, tableMeta) => {
			// Get the raw data to check the actual boolean values
			const rowIndex = tableMeta.rowIndex
			const rawData = datatable.data[rowIndex] && datatable.data[rowIndex]._rawData || {}
			
			// Get the column name from tableMeta
			const columnIndex = tableMeta.columnIndex
			
			// Calculate the final columns structure (same as what's sent to DataTable)
			const nomEntrepriseIndex = datatable.columns.findIndex(col => col.name === 'nomEntreprise')
			const insertIndex = nomEntrepriseIndex !== -1 ? nomEntrepriseIndex + 1 : datatable.columns.length
			const finalColumns = [...datatable.columns]
			finalColumns.splice(insertIndex, 0, typeColumn)
			finalColumns.push(actionsColumn)
			
			const columnName = finalColumns[columnIndex] && finalColumns[columnIndex].name
			
			// Only process invitation columns, not date columns
			if (columnName !== 'invitationInspectionCommune' && columnName !== 'invitationReunionCoordination') {
				return value // Return as-is for non-boolean columns
			}
			
			// Get the actual boolean value from raw data
			const actualValue = rawData[columnName]
			
			// Use the raw value instead of the displayed value
			const boolValue = actualValue
			
			if (boolValue === true || boolValue === 'Acceptée' || boolValue === 'true')
				return (
					<div className={classes.div}>
						<Icon className={classes.validated}>check_circle</Icon>
					</div>
				)
			else if (boolValue === false || boolValue === 'Refusée' || boolValue === 'false')
				return (
					<div className={classes.div}>
						<Icon className={classes.refuse}>remove_circle</Icon>
					</div>
				)
			else if (boolValue === null || boolValue === undefined || boolValue === '' || boolValue === 'Vide')
				return (
					<div className={classes.div}>
						{/* Aucun icône pour l'état vide */}
					</div>
				)
			else
				return (
					<div className={classes.div}>
						<Icon style={{ color: '#888888' }}>help</Icon>
					</div>
				)
		}

		const customTypeColumn = (value, tableMeta) => {
			// Get the raw data from the transformed data using the row index
			const rowIndex = tableMeta.rowIndex
			const rawData = datatable.data[rowIndex] && datatable.data[rowIndex]._rawData || {}
			
			const entreprisePrincipal = rawData.entreprisePrincipal
			const entrepriseSousTraitante = rawData.entrepriseSousTraitante
			
			let typeIcon
			if (entreprisePrincipal && entrepriseSousTraitante) {
				typeIcon = <img src={TYPES.princsub.icon} alt={TYPES.princsub.code} style={{ width: '40px', height: '42px' }} />
			} else if (entreprisePrincipal) {
				typeIcon = <img src={TYPES.principale.icon} alt={TYPES.principale.code} style={{ width: '40px', height: '42px' }} />
			} else if (entrepriseSousTraitante) {
				typeIcon = <img src={TYPES.subcontract.icon} alt={TYPES.subcontract.code} style={{ width: '40px', height: '42px' }} />
			} else {
				typeIcon = '-'
			}
			
			return (
				<div className={classes.div}>
					{typeIcon}
				</div>
			)
		}

		const customActionsColumn = (_value, tableMeta) => {
			if (!tableMeta) return
			// Get the raw data using the same method as other custom columns
			const rowIndex = tableMeta.rowIndex
			const rawData = datatable.data[rowIndex] && datatable.data[rowIndex]._rawData || {}
			const pdpId = rawData.pdpId
			const rpdNumero = rawData.rpdNumero
			const entId = rawData.entId

			const LinkIcon = ({ icon, toolTip, to, onClick }) => (
				<Tooltip title={toolTip}>
					<Link onClick={onClick} to={to} target={'_blank'} style={{ textDecoration: 'none' }}>
						<IconButton>
							<Icon>{icon}</Icon>
						</IconButton>
					</Link>
				</Tooltip>
			)
			LinkIcon.propTypes = {
				icon: PropTypes.string,
				toolTip: PropTypes.string,
				to: PropTypes.string,
				onClick: PropTypes.func,
			}

			return (
				<>
					<LinkIcon
						icon="edit"
						toolTip={t('EditConvocation')}
						to={`/convocation/${pdpId}/${rpdNumero}/${entId}`}
					/>
				</>
			)
		}

		const customColumns = [
			{
				name: 'type',
				component: customTypeColumn,
			},
			{
				name: 'invitationInspectionCommune',
				component: customBooleanColumn,
			},
			{
				name: 'invitationReunionCoordination',
				component: customBooleanColumn,
			},
			{
				name: 'derniereModification',
				component: customDateColumn,
			},
			{
				name: 'actions',
				component: customActionsColumn,
			},
		]
		//#endregion Custom Columns

		return (
			<>
				{isLoading && (
					<CircularProgress disableShrink color="secondary" className={classes.buttonProgress} />
				)}
				{!isLoading && (
					<div className={classes.dataTableContainer}>
						<DataTable
							title={t('FollowConvoc')}
							columns={(() => {
								// Find the index where to insert the type column (after nomEntreprise)
								const nomEntrepriseIndex = datatable.columns.findIndex(col => col.name === 'nomEntreprise')
								const insertIndex = nomEntrepriseIndex !== -1 ? nomEntrepriseIndex + 1 : datatable.columns.length
								
								// Create new columns array with type inserted at the right position
								const newColumns = [...datatable.columns]
								newColumns.splice(insertIndex, 0, typeColumn)
								newColumns.push(actionsColumn)
								
								return newColumns
							})()}
							data={datatable.data}
							options={datatable.options}
							customColumns={customColumns}
							isLoading={isLoading}
							customLabels={[
								{
									name: 'numeroPdp',
									label: t('pdpNumero'),
								},
								{
									name: 'libellePdP',
									label: t('pdp_description'),
								},
								{
									name: 'descriptionLieuxPdP',
									label: t('works_locations'),
								},
								{
									name: 'principalName',
									label: t('principal'),
								},
								{
									name: 'natureIntervention',
									label: t('natureIntervention'),
								},
								{
									name: 'nomEntreprise',
									label: t('entRaisonSociale'),
								},
								{
									name: 'type',
									label: t('companycontracttype'),
								},
								{
									name: 'invitationInspectionCommune',
									label: t('visitePrealable'),
								},
								{
									name: 'invitationReunionCoordination',
									label: t('CoordinationMeeting'),
								},
								{
									name: 'dateInspectionPrealable',
									label: 'Date Inspection Préalable',
								},
								{
									name: 'dateReunionCoordination',
									label: 'Date Réunion Coordination',
								},
								{
									name: 'lieuInspectionPrealable',
									label: 'Lieu Inspection Préalable',
								},
								{
									name: 'lieuReunionCoordination',
									label: 'Lieu Réunion Coordination',
								},
								{
									name: 'entreprisePrincipal',
									label: 'Entreprise Principal',
								},
								{
									name: 'entrepriseSousTraitante',
									label: 'Entreprise Sous-Traitante',
								},
								{
									name: 'pdpId',
									label: 'PDP ID',
								},
								{
									name: 'entId',
									label: 'Entreprise ID',
								},
								{
									name: 'rpdNumero',
									label: 'RPD Numéro',
								},
								{
									name: 'derniereModification',
									label: t('derniereModification'),
								},
								{
									name: 'actions',
									label: t('Actions'),
								},
							]}
						/>
					</div>
				)}
			</>
		)
	}

	//#region Effects
	useEffect(() => {
		if (actualView || actualView === 0) getConvocationFollowUp()
	}, [actualView])

	useEffect(() => {
		setActualView(0) // Set default view
	}, [])
	//#endregion

	return (
		<>
			{typeModal && (
				<TotalPopUpWarning
					open={openModal}
					onClose={closeModal}
					content={typeModal.content}
					title={typeModal.title}
				/>
			)}
			<HeadTitle title={t('FollowConvoc')} link="/dashboard" />
			<FollowTable />
		</>
	)
}

export default FollowConvocation
